<script lang="ts">
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices_config } from 'src/configs/devices';
	import { real_id, is_gen_2 } from 'src/helpers/interface_helpers';
	import { checkDeviceForceUpdate, getDeviceMinFwVersion } from 'src/helpers/firmware_helpers';
	import { SidepanelContent } from '@Shelly/shelly-design-system';
	import DeviceMainStateView from 'src/components/Sidepanel/DeviceMainStateView.svelte';
	import MandatoryFirmwareUpdateAlert from 'src/components/Alerts/MandatoryFirmwareUpdateAlert.svelte';
	import { setDeviceViewStateContext } from 'src/components/Sidepanel/device-view-state-context';
	import type { Device } from 'src/types/Device.ts';

	/**
	 * Device view wrapper that centralizes common state derivation.
	 *
	 * Derives `status`, `settings`, and `deviceConfig`, computes `hasAllData`,
	 * and renders the standard `DeviceMainStateView` + `SidepanelContent` layout.
	 * Use the named slots to provide view-specific UI (`staticTop`, `content`, `staticBottom`).
	 */

	export let device: Device;
	export let hideSlotStaticTop = false;
	export let hideSlotStaticBottom = false;
	export let ignoreOfflineState = false;

	const deviceConfig = devices_config?.[device?.type];

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: fwVersion = (is_gen_2(device) ? settings?.sys?.device?.fw_id : settings?.fw) ?? '';
	$: minVersion = getDeviceMinFwVersion(device) ?? '';
	$: showMandatoryUpdateAlert = settings && checkDeviceForceUpdate(device) && status?.online;
	$: setDeviceViewStateContext({ showMandatoryUpdateAlert: !!showMandatoryUpdateAlert });

	$: hasAllData = !!(
		device &&
		status !== undefined &&
		settings !== undefined &&
		deviceConfig !== undefined
	);
</script>

<DeviceMainStateView {device} {status} {hasAllData} {ignoreOfflineState}>
	<SidepanelContent
		staticBottomTopSpacing="m"
		hideSlotStaticBottom={!$$slots['staticBottom']}
		{hideSlotStaticTop}
	>
		<svelte:fragment slot="staticTop">
			{#if showMandatoryUpdateAlert}
				<MandatoryFirmwareUpdateAlert
					{device}
					values={{ fwVersion, minVersion }}
					isOffline={!status?.online}
				/>
			{/if}
			<slot name="staticTop" />
		</svelte:fragment>
		<slot name="content" />
		<svelte:fragment slot="staticBottom">
			<slot name="staticBottom" />
		</svelte:fragment>
	</SidepanelContent>
</DeviceMainStateView>
